<template>
  <div class="flex flex-col w-full mr-0 md:mr-10">
    <label class="block mb-2 text-sm font-medium text-custom_gray dark:text-white">
      {{ label }}
    </label>
    <div class="flex w-full">
      <fwb-range
        class="tuin-range"
        :model-value="modelValue"
        @update:model-value="updateValue"
        size="lg"
        :max="max"
        :min="min"
        :style="dynamicStyle"
        :label="rangeLabel"
      />
    </div>
    <div class="flex w-full mt-4 text-custom_gray">
      {{ displayText }}: <strong>{{ modelValue + "%" }}</strong>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { FwbRange } from "flowbite-vue"

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  modelValue: {
    type: Number,
    required: true,
    default: 1
  },
  max: {
    type: Number,
    default: 100
  },
  min: {
    type: Number,
    default: 1
  },
  rangeLabel: {
    type: String,
    default: "Porcentaje sobre el valor total de la operación"
  },
  displayText: {
    type: String,
    default: "Porcentaje a compartir"
  }
})

const emit = defineEmits(['update:modelValue'])

const dynamicStyle = computed(() => ({
  '--value': props.modelValue + '%'
}))

function updateValue(value) {
  emit('update:modelValue', value)
}
</script>

<style scoped>
/* =====================
   Rango personalizado tuin
   ===================== */
/* Aseguramos alcance al input dentro de fwb-range usando :deep y clase externa */
:deep(.tuin-range input[type=range]) {
  accent-color: #FF4265; /* fallback simple */
  --tuin-active: #FF4265;
  --tuin-inactive: #e5e7eb;
  width: 100%;
  background: transparent;
  cursor: pointer;
}

/* Track WebKit con gradiente dinámico basado en valor (usamos atributo value para calcular via JS si quisiéramos luego) */
:deep(.tuin-range input[type=range]::-webkit-slider-runnable-track) {
  height: 8px;
  border-radius: 4px;
  /* Usamos gradiente para porción activa (hasta --value) y resto inactivo */
  background: linear-gradient(to right, var(--tuin-active) 0%, var(--tuin-active) var(--value, 0%), var(--tuin-inactive) var(--value, 0%), var(--tuin-inactive) 100%);
  position: relative;
}

/* Simulamos relleno activo añadiendo una pseudo-capa: no soportado directamente, así que cambiamos el background via inline style luego opcionalmente */
/* Thumb WebKit */
:deep(.tuin-range input[type=range]::-webkit-slider-thumb) {
  -webkit-appearance: none;
  height: 20px;
  width: 20px;
  margin-top: -6px; /* centra sobre track 8px */
  border-radius: 50%;
  background: #FFFFFF; /* fill */
  border: 1px solid #E5E7EB; /* stroke gray-200 */
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.08)); /* subtle shadow */
}

/* Firefox Track + Progress */
:deep(.tuin-range input[type=range]::-moz-range-track) {
  height: 8px;
  border-radius: 4px;
  background: var(--tuin-inactive);
}
:deep(.tuin-range input[type=range]::-moz-range-progress) {
  height: 8px;
  border-radius: 4px 0 0 4px;
  background: var(--tuin-active);
}
:deep(.tuin-range input[type=range]::-moz-range-thumb) {
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: #FFFFFF; /* fill */
  border: 1px solid #E5E7EB; /* stroke */
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.08));
}

/* Edge (Chromium) usa WebKit, Safari también cubierto */

/* JS opcional: pintado activo en WebKit mediante inline style dinámico si se requiere más adelante */

/* Personalización del fwb-range (Flowbite Vue Range) */
:deep(.fwb-range input[type=range]) { cursor:pointer; }
:deep(.fwb-range input[type=range]::-webkit-slider-thumb) {
  -webkit-appearance: none;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: #FFFFFF; /* fill */
  border: 1px solid #E5E7EB; /* stroke */
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.08));
  margin-top: -6px; /* (thumb 20 - track 8) /2 */
}

/* Firefox */
:deep(.fwb-range input[type=range]::-moz-range-thumb) {
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.08));
}

/* Track colores (por compatibilidad) */
:deep(.fwb-range input[type=range]::-webkit-slider-runnable-track) {
  height:8px; border-radius:4px; background: linear-gradient(to right,#FF4265 0%,#FF4265 var(--value,50%),#e5e7eb var(--value,50%),#e5e7eb 100%);
}
:deep(.fwb-range input[type=range]::-moz-range-track) {
  height:8px; border-radius:4px; background:#e5e7eb;
}
:deep(.fwb-range input[type=range]::-moz-range-progress) {
  height:8px; border-radius:4px; background:#FF4265;
}
</style>